import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * The Selection tab strip (T-0142, T-0267; E14-design §1): Requirements,
 * then Vendor intelligence when the engagement's Selection module is on,
 * then Vendor evaluation, still disabled until vendors' RFP responses can
 * be graded against the RTM. One direction: requirements, then vendors and
 * ratings, then the report.
 */

export type SelectionTab = 'requirements' | 'vendor-intelligence';

export const VENDOR_EVALUATION_HINT =
  "Upload vendors' RFP responses and grade them against the RTM: coming later";

const TAB =
  '-mb-px inline-flex items-center gap-2 border-b-2 pb-2.5 pt-2 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

export function SelectionTabs({
  active,
  requirementsCount,
  showVendorIntelligence,
  onSelect,
}: {
  active: SelectionTab;
  /** Shown beside Requirements when there are any. */
  requirementsCount?: number;
  /** The engagement's Selection module is on. */
  showVendorIntelligence: boolean;
  onSelect?: (tab: SelectionTab) => void;
}) {
  const tab = (id: SelectionTab, label: string, extra?: React.ReactNode) => {
    const on = active === id;
    return (
      <button
        type="button"
        role="tab"
        aria-selected={on}
        onClick={() => (on ? undefined : onSelect?.(id))}
        className={cn(
          TAB,
          on
            ? 'border-accent-amber-600 font-semibold text-ink-900'
            : 'border-transparent font-medium text-ink-500 hover:text-ink-900',
        )}
      >
        {label}
        {extra}
      </button>
    );
  };
  return (
    <div role="tablist" aria-label="Selection" className="flex gap-6 px-6">
      {tab(
        'requirements',
        'Requirements',
        requirementsCount && requirementsCount > 0 ? (
          <span className="rounded-full bg-ink-100 px-1.5 py-px text-[11px] font-medium text-ink-500">
            {requirementsCount.toLocaleString()}
          </span>
        ) : null,
      )}
      {showVendorIntelligence ? tab('vendor-intelligence', 'Vendor intelligence') : null}
      <button
        type="button"
        role="tab"
        aria-selected="false"
        aria-disabled="true"
        title={VENDOR_EVALUATION_HINT}
        className={cn(TAB, 'cursor-default border-transparent font-medium text-ink-300')}
      >
        Vendor evaluation
      </button>
    </div>
  );
}
